import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from "@fleet-os/api-client";
import './TeamMembers.css'; // Reusing the same premium CSS

const API_BASE = `${API_BASE_URL}/api/team`;

function AzureGroupTable({ groupId, role, members }) {
  const [azureMembers, setAzureMembers] = useState([]);
  const [isAzureLoading, setIsAzureLoading] = useState(false);

  useEffect(() => {
    const fetchAzureMembers = async () => {
      try {
        setIsAzureLoading(true);
        const res = await fetch(`${API_BASE}/azure-group/${groupId}`);
        if (res.ok) {
          const data = await res.json();
          setAzureMembers(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsAzureLoading(false);
      }
    };
    fetchAzureMembers();
  }, [groupId]);

  return (
    <div className="azure-members-list" style={{ marginTop: '16px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
      <h5 style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '12px' }}>Active Members in Azure AD:</h5>
      {isAzureLoading ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading from Azure...</div>
      ) : azureMembers.length > 0 ? (
        <table className="tm-table" style={{ marginTop: 0 }}>
          <thead>
            <tr>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>ROLE</th>
              <th>CSM</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {azureMembers.map(m => {
              const localUser = members.find(local => local.oid === m.id);
              const displayRole = localUser ? localUser.role : role;
              const displayCsm = localUser ? (localUser.csm === 1 ? "Yes" : "No") : "-";
              const displayStatus = localUser ? localUser.status : "Pending Login";
              
              return (
                <tr key={m.id}>
                  <td className="tm-name">{m.name || '-'}</td>
                  <td className="tm-email">{m.email}</td>
                  <td>
                    <span className={`tm-role-badge role-${displayRole.toLowerCase()}`}>
                      {displayRole}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{displayCsm}</span>
                  </td>
                  <td>
                    <span className={`tm-status ${displayStatus === 'Pending Login' ? 'disabled' : displayStatus.toLowerCase()}`}>
                      {displayStatus}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No members found or missing Graph API permissions.</div>
      )}
    </div>
  );
}

function Settings() {
  const [members, setMembers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form states for new mapping
  const [newGroupId, setNewGroupId] = useState('');
  const [newRole, setNewRole] = useState('Viewer');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [membersRes, mappingsRes] = await Promise.all([
        fetch(`${API_BASE}/members`),
        fetch(`${API_BASE}/mappings`)
      ]);
      const membersData = await membersRes.json();
      const mappingsData = await mappingsRes.json();
      setMembers(membersData);
      setMappings(mappingsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Disabled' : 'Active';
    try {
      await fetch(`${API_BASE}/members/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleCsm = async (id, currentCsm) => {
    try {
      await fetch(`${API_BASE}/members/${id}/csm`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csm: !currentCsm })
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const addMapping = async () => {
    if (!newGroupId.trim()) return;
    try {
      await fetch(`${API_BASE}/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: newGroupId.trim(), role: newRole })
      });
      setNewGroupId('');
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteMapping = async (id) => {
    try {
      await fetch(`${API_BASE}/mappings/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const updateMappingRole = async (id, groupId, groupName, role) => {
    try {
      await fetch(`${API_BASE}/mappings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId, group_name: groupName, role })
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return <div className="tm-loading">Loading Settings...</div>;
  }

  return (
    <div className="tm-container fade-in">
      <div className="tm-header">
        <h1>Settings</h1>
        <p>Manage application settings, team members, and Microsoft Entra SSO Role mappings.</p>
      </div>

      <div className="tm-card">
        <div className="sso-header" style={{ marginBottom: '20px' }}>
          <h3><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg> Team Members</h3>
          <p>Users who have logged into Fleet OS.</p>
        </div>
        <table className="tm-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>ROLE</th>
              <th>CSM</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => (
              <tr key={member.id}>
                <td className="tm-name">{member.name || '-'}</td>
                <td className="tm-email">{member.email}</td>
                <td>
                  <span className={`tm-role-badge role-${member.role.toLowerCase()}`}>
                    {member.role}
                  </span>
                </td>
                <td>
                  <label className="tm-checkbox">
                    <input 
                      type="checkbox" 
                      checked={member.csm === 1}
                      onChange={() => toggleCsm(member.id, member.csm === 1)}
                    />
                    <span>CSM</span>
                  </label>
                </td>
                <td>
                  <span className={`tm-status ${member.status.toLowerCase()}`}>
                    {member.status}
                  </span>
                </td>
                <td>
                  <button 
                    className={`tm-action-btn ${member.status === 'Active' ? 'disable' : 'enable'}`}
                    onClick={() => toggleStatus(member.id, member.status)}
                  >
                    {member.status === 'Active' ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan="6" className="tm-empty">No team members found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="tm-card sso-card">
        <div className="sso-header">
          <h3><svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> SSO Role Mappings (Microsoft Entra)</h3>
          <p>Map Microsoft Entra security groups to Fleet OS roles. When a user signs in with Microsoft, their group memberships determine their role here.</p>
        </div>

        <div className="sso-default-admin" style={{ marginBottom: '32px' }}>
          <h4>Default Admin Group (hardcoded)</h4>
          <span className="sso-group-id">4f7850cb-0707-4b6f-a52e-28d591db352a</span>
          <p>Members of this group are always granted admin access. This cannot be changed here.</p>
          <AzureGroupTable groupId="4f7850cb-0707-4b6f-a52e-28d591db352a" role="Admin" members={members} />
        </div>

        <div className="sso-mappings-list">
          {mappings.map(map => (
            <div className="sso-mapping-row-container" key={map.id} style={{ marginBottom: '32px' }}>
              <div className="sso-mapping-row" style={{ marginBottom: '8px' }}>
                <div className="sso-input-wrapper">
                  <input type="text" value={map.group_id} readOnly />
                </div>
                <div className="sso-select-wrapper">
                  <select 
                    value={map.role} 
                    onChange={(e) => updateMappingRole(map.id, map.group_id, map.group_name, e.target.value)}
                  >
                    <option value="Admin">Admin</option>
                    <option value="Manager">Manager</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>
                <button className="sso-delete-btn" onClick={() => deleteMapping(map.id)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
              <AzureGroupTable groupId={map.group_id} role={map.role} members={members} />
            </div>
          ))}

          {/* Add New Mapping */}
          <div className="sso-mapping-row add-row">
            <div className="sso-input-wrapper">
              <input 
                type="text" 
                placeholder="Enter Entra Group Object ID" 
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.target.value)}
              />
            </div>
            <div className="sso-select-wrapper">
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="Viewer">Viewer</option>
              </select>
            </div>
            <button className="sso-add-btn" onClick={addMapping} disabled={!newGroupId.trim()}>
              + Add Role Mapping
            </button>
          </div>
        </div>

        <div className="sso-footer-help">
          <h5>How it works:</h5>
          <ul>
            <li>When a user signs in with Microsoft, we check their Entra group memberships.</li>
            <li>The highest-priority matching role wins (Admin &gt; Manager &gt; Viewer).</li>
            <li>If no group matches, the user defaults to Viewer.</li>
            <li>Users are auto-created on first sign-in — no need to add them manually.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Settings;
